import React, { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import configuracionInicialVacacionesService from '../services/configuracionInicialVacacionesService';

const normalizarDoc = (v) => String(v || '').replace(/\D+/g, '');

const normalizarClave = (k) =>
  String(k || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .trim();

const esReciboHonorarios = (empleado) => {
  const tipo = String(empleado?.tipo_contratacion || '').trim().toLowerCase();
  if (!tipo) return false;
  return tipo.includes('honorario') || tipo === 'rh' || tipo === 'recibo por honorarios';
};

const etiquetaTipoContratacion = (empleado) => {
  const tipo = String(empleado?.tipo_contratacion || '').trim();
  if (!tipo) return 'Sin tipo';
  if (esReciboHonorarios(empleado)) return 'Recibo por Honorarios';
  if (tipo.toLowerCase() === 'interno') return 'Interno';
  if (tipo.toLowerCase() === 'planilla') return 'Planilla';
  return tipo;
};

/**
 * Modal: importar saldos iniciales de vacaciones por Excel.
 * Columnas: numero_documento | dias | tipo_contratacion | observaciones | nombre_completo
 */
const ImportadorSaldosVacaciones = ({
  isOpen,
  onClose,
  empresaId,
  empleados = [],
  saldosActuales = {},
  onImportado,
}) => {
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState([]);
  const [erroresLocales, setErroresLocales] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [modo, setModo] = useState('upsert');
  /** todos | planilla | recibo — filtro al descargar plantilla */
  const [filtroPlantilla, setFiltroPlantilla] = useState('todos');

  const empleadosVisibles = useMemo(
    () =>
      (empleados || []).filter(
        (e) => !e.es_boleta_liquidada && !(e.ultimo_historial && e.ultimo_historial.fecha_cese)
      ),
    [empleados]
  );

  const empleadosParaPlantilla = useMemo(() => {
    let lista = empleadosVisibles;
    if (filtroPlantilla === 'recibo') {
      lista = lista.filter((e) => esReciboHonorarios(e));
    } else if (filtroPlantilla === 'planilla') {
      lista = lista.filter((e) => !esReciboHonorarios(e));
    }
    return [...lista].sort((a, b) => {
      const ta = etiquetaTipoContratacion(a);
      const tb = etiquetaTipoContratacion(b);
      if (ta !== tb) return ta.localeCompare(tb, 'es');
      return String(a.nombre_completo || '').localeCompare(String(b.nombre_completo || ''), 'es');
    });
  }, [empleadosVisibles, filtroPlantilla]);

  const conteoPlantilla = useMemo(() => {
    const planilla = empleadosVisibles.filter((e) => !esReciboHonorarios(e)).length;
    const recibo = empleadosVisibles.filter((e) => esReciboHonorarios(e)).length;
    return { planilla, recibo, todos: empleadosVisibles.length };
  }, [empleadosVisibles]);

  const mapaEmpleadosPorDoc = useMemo(() => {
    const map = {};
    empleadosVisibles.forEach((e) => {
      const doc = normalizarDoc(e.numero_documento || e.documento);
      if (doc) map[doc] = e;
    });
    return map;
  }, [empleadosVisibles]);

  const reset = () => {
    setArchivo(null);
    setPreview([]);
    setErroresLocales([]);
    setResultado(null);
    setProcesando(false);
    setGuardando(false);
    setFiltroPlantilla('todos');
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const descargarPlantilla = useCallback(() => {
    const headers = [
      'numero_documento',
      'dias',
      'tipo_contratacion',
      'observaciones',
      'nombre_completo',
    ];
    const filas = empleadosParaPlantilla.map((emp) => [
      emp.numero_documento || emp.documento || '',
      saldosActuales[emp.id] != null ? saldosActuales[emp.id] : '',
      etiquetaTipoContratacion(emp),
      '',
      emp.nombre_completo || emp.nombre || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...filas]);
    ws['!cols'] = [{ wch: 16 }, { wch: 8 }, { wch: 24 }, { wch: 40 }, { wch: 36 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Saldos Vacaciones');
    const fecha = new Date().toISOString().slice(0, 10);
    const sufijo =
      filtroPlantilla === 'recibo'
        ? '_recibo_honorarios'
        : filtroPlantilla === 'planilla'
          ? '_planilla'
          : '';
    XLSX.writeFile(wb, `plantilla_saldos_vacaciones${sufijo}_${fecha}.xlsx`);
  }, [empleadosParaPlantilla, saldosActuales, filtroPlantilla]);

  const leerArchivo = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
      reader.readAsArrayBuffer(file);
    });

  const mapearFila = (row) => {
    const keys = Object.keys(row || {});
    const byNorm = {};
    keys.forEach((k) => {
      byNorm[normalizarClave(k)] = row[k];
    });

    const doc =
      byNorm.numero_documento
      ?? byNorm.documento
      ?? byNorm.dni
      ?? byNorm.nro_documento
      ?? byNorm.n_documento
      ?? '';
    const diasRaw =
      byNorm.dias
      ?? byNorm.dias_saldo
      ?? byNorm.saldo_inicial
      ?? byNorm.saldo
      ?? '';
    const obs =
      byNorm.observaciones
      ?? byNorm.observacion
      ?? byNorm.comentario
      ?? '';
    const nombre =
      byNorm.nombre_completo
      ?? byNorm.nombre
      ?? byNorm.empleado
      ?? '';
    const tipo =
      byNorm.tipo_contratacion
      ?? byNorm.tipo
      ?? byNorm.modalidad
      ?? '';

    return {
      documento: String(doc).trim(),
      dias: diasRaw === '' || diasRaw == null ? null : Number(diasRaw),
      observaciones: String(obs || '').trim() || null,
      nombre_completo: String(nombre || '').trim() || null,
      tipo_contratacion: String(tipo || '').trim() || null,
    };
  };

  const procesarArchivo = async (file) => {
    setProcesando(true);
    setErroresLocales([]);
    setPreview([]);
    setResultado(null);
    try {
      const rows = await leerArchivo(file);
      if (!rows.length) {
        setErroresLocales(['El archivo no tiene filas de datos.']);
        return;
      }

      const errores = [];
      const ok = [];
      const vistos = new Set();

      rows.forEach((row, idx) => {
        const fila = idx + 2; // + header
        const mapped = mapearFila(row);
        const docNorm = normalizarDoc(mapped.documento);

        if (!docNorm) {
          errores.push(`Fila ${fila}: falta numero_documento`);
          return;
        }
        if (mapped.dias == null || Number.isNaN(mapped.dias) || mapped.dias < 0 || mapped.dias > 999) {
          errores.push(`Fila ${fila} (${mapped.documento}): días inválidos (0–999)`);
          return;
        }
        if (!Number.isInteger(mapped.dias)) {
          errores.push(`Fila ${fila} (${mapped.documento}): días debe ser entero`);
          return;
        }
        if (vistos.has(docNorm)) {
          errores.push(`Fila ${fila}: documento duplicado ${mapped.documento}`);
          return;
        }
        vistos.add(docNorm);

        const emp = mapaEmpleadosPorDoc[docNorm];
        ok.push({
          ...mapped,
          documento_norm: docNorm,
          empleado_id: emp?.id || null,
          empleado_nombre: emp?.nombre_completo || mapped.nombre_completo || '—',
          tipo_contratacion: emp ? etiquetaTipoContratacion(emp) : (mapped.tipo_contratacion || '—'),
          encontrado: Boolean(emp),
          saldo_actual: emp ? (saldosActuales[emp.id] ?? 0) : null,
        });
      });

      const noEncontrados = ok.filter((r) => !r.encontrado);
      noEncontrados.forEach((r) => {
        errores.push(`Documento ${r.documento}: no está en la empresa / lista activa`);
      });

      setPreview(ok.filter((r) => r.encontrado));
      setErroresLocales(errores);
    } catch (err) {
      setErroresLocales([err.message || 'Error al procesar el Excel']);
    } finally {
      setProcesando(false);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setErroresLocales(['Formato no válido. Use .xlsx, .xls o .csv']);
      return;
    }
    setArchivo(file);
    procesarArchivo(file);
  };

  const confirmarImportacion = async () => {
    if (!empresaId || preview.length === 0) return;
    setGuardando(true);
    setResultado(null);
    try {
      const filas = preview.map((r) => ({
        documento: r.documento,
        dias: r.dias,
        observaciones: r.observaciones,
      }));
      const response = await configuracionInicialVacacionesService.importarMasivo(empresaId, {
        filas,
        modo,
      });
      setResultado(response);
      if (response?.success) {
        await onImportado?.(response);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || err?.message
        || 'Error al guardar la importación';
      setErroresLocales([msg]);
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Importar saldos iniciales de vacaciones</h2>
            <p className="text-purple-100 text-sm">
              Excel con documento y días a restar del cálculo automático
            </p>
          </div>
          <button type="button" onClick={handleClose} className="text-white hover:bg-white/20 rounded-lg px-3 py-1 text-xl">
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-900">
            Columnas: <strong>numero_documento</strong>, <strong>dias</strong>,{' '}
            <strong>tipo_contratacion</strong> (informativo), observaciones (opcional), nombre_completo.
            Los días del saldo inicial se restan de los días calculados (empleados antiguos).
            Puedes filtrar la plantilla por Planilla o Recibo por Honorarios antes de descargarla.
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descargar plantilla por tipo
              </label>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={filtroPlantilla}
                onChange={(e) => setFiltroPlantilla(e.target.value)}
                disabled={guardando}
              >
                <option value="todos">Todos ({conteoPlantilla.todos})</option>
                <option value="planilla">Solo Planilla ({conteoPlantilla.planilla})</option>
                <option value="recibo">Solo Recibo por Honorarios ({conteoPlantilla.recibo})</option>
              </select>
            </div>
            <button
              type="button"
              onClick={descargarPlantilla}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg"
            >
              Descargar plantilla ({empleadosParaPlantilla.length})
            </button>
            <label className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg cursor-pointer">
              {procesando ? 'Leyendo…' : 'Seleccionar Excel'}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={onFileChange}
                disabled={procesando || guardando}
              />
            </label>
            {archivo && (
              <span className="text-sm text-gray-600 truncate max-w-xs">{archivo.name}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modo</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={modo}
              onChange={(e) => setModo(e.target.value)}
              disabled={guardando}
            >
              <option value="upsert">Actualizar existentes y crear nuevos</option>
              <option value="solo_nuevos">Solo crear (no tocar existentes)</option>
            </select>
          </div>

          {erroresLocales.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 max-h-40 overflow-y-auto">
              <p className="font-semibold mb-1">Avisos / errores ({erroresLocales.length})</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {erroresLocales.slice(0, 30).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {erroresLocales.length > 30 && (
                  <li>… y {erroresLocales.length - 30} más</li>
                )}
              </ul>
            </div>
          )}

          {preview.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">
                Vista previa: {preview.length} fila(s) listas para importar
              </div>
              <div className="max-h-64 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Documento</th>
                      <th className="px-3 py-2 text-left">Empleado</th>
                      <th className="px-3 py-2 text-left">Tipo contratación</th>
                      <th className="px-3 py-2 text-right">Saldo actual</th>
                      <th className="px-3 py-2 text-right">Nuevo (días)</th>
                      <th className="px-3 py-2 text-left">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r) => (
                      <tr key={r.documento_norm} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-mono">{r.documento}</td>
                        <td className="px-3 py-2">{r.empleado_nombre}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                            String(r.tipo_contratacion || '').toLowerCase().includes('honorario')
                              ? 'bg-sky-100 text-sky-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {r.tipo_contratacion || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500">{r.saldo_actual ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-purple-700">{r.dias}</td>
                        <td className="px-3 py-2 text-gray-600 truncate max-w-[200px]">
                          {r.observaciones || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {resultado?.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
              <p className="font-semibold">{resultado.message}</p>
              <p className="mt-1 text-xs">
                Creados: {resultado.data?.creados ?? 0} · Actualizados: {resultado.data?.actualizados ?? 0}
                {(resultado.data?.omitidos || 0) > 0 ? ` · Omitidos: ${resultado.data.omitidos}` : ''}
                {(resultado.data?.fallidos?.length || 0) > 0
                  ? ` · Fallidos: ${resultado.data.fallidos.length}`
                  : ''}
              </p>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            disabled={guardando}
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={confirmarImportacion}
            disabled={guardando || preview.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {guardando ? 'Importando…' : `Importar ${preview.length || ''} saldo(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportadorSaldosVacaciones;
